import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';

/** Пастельные цвета этапов — как на сайте */
export const STAGE_COLORS: Record<string, string> = {
  pink: '#f6c3d3',
  blue: '#bcd0f2',
  home: '#a8c38a',
  learn: '#cfc8f5',
  docs: '#f9cdb0',
  perks: '#f7da6e',
};
export const STAGE_COLOR_KEYS = Object.keys(STAGE_COLORS);

const YEREVAN: [number, number] = [40.1792, 44.4991];

export interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  title: string;
  sub?: string | null;
  /** заливка точки */
  color: string;
  /** подключённый партнёр — крупнее, со сливовой обводкой */
  strong?: boolean;
  dim?: boolean;
}

/**
 * Карта на OpenStreetMap: точки-кружки (без картинок-маркеров), клик по точке — onPick.
 * Если точек нет — Ереван целиком.
 */
export function MapView({ points, height = 420, onPick, focus }: { points: MapPoint[]; height?: number; onPick?: (id: string) => void; focus?: [number, number] | null }) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const pick = useRef(onPick);
  pick.current = onPick;

  useEffect(() => {
    if (!box.current) return;
    const m = L.map(box.current, { zoomControl: true, attributionControl: true }).setView(YEREVAN, 12);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(m);
    map.current = m;
    layer.current = L.layerGroup().addTo(m);
    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    const g = layer.current;
    if (!m || !g) return;
    g.clearLayers();
    for (const p of points) {
      const c = L.circleMarker([p.lat, p.lng], {
        radius: p.strong ? 10 : 8,
        color: p.strong ? '#4a1028' : '#5c5550',
        weight: p.strong ? 3 : 1.5,
        fillColor: p.color,
        fillOpacity: p.dim ? 0.45 : 0.95,
      });
      c.bindTooltip(`<b>${escape(p.title)}</b>${p.sub ? `<br>${escape(p.sub)}` : ''}`, { direction: 'top', offset: [0, -6] });
      c.on('click', () => pick.current?.(p.id));
      c.addTo(g);
    }
    if (focus) m.setView(focus, 16);
    else if (points.length) m.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number])).pad(0.25), { maxZoom: 15 });
  }, [points, focus]);

  return <div ref={box} className="bo-map" style={{ height }} />;
}

const escape = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** Координаты из «40.18, 44.51» или ссылки Google/Яндекс-карт */
export function parseCoords(raw: string): { lat: number; lng: number } | null {
  const s = decodeURIComponent(raw.trim());
  const patterns = [/@(-?\d+\.\d+),(-?\d+\.\d+)/, /[?&](?:q|query)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/, /^(-?\d+\.\d+)\s*[,; ]\s*(-?\d+\.\d+)$/, /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/];
  for (const re of patterns) {
    const m = s.match(re);
    if (m) {
      const lat = Number(m[1]);
      const lng = Number(m[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
    }
  }
  // Яндекс: ll=lng,lat
  const y = s.match(/ll=(-?\d+\.\d+)%2C(-?\d+\.\d+)|ll=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (y) return { lat: Number(y[2] ?? y[4]), lng: Number(y[1] ?? y[3]) };
  return null;
}
